import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import './NewHome.css';

/* ============================================================
 * 로고 경로: C:\Git\newhome\frontend\public\logo
 * 파트너사 로고 92개는 alt 텍스트(회사/기관명) 그대로 파일명으로 사용합니다.
 * (예: alt="HD한국조선해양" → /logo/HD한국조선해양.png)
 * ============================================================ */
const LOGO_BASE = '/logo/';

// public/logo 폴더의 실제 파일명 규칙(로고 추출 시 sanitize 로직과 동일):
// 영문/숫자/하이픈/한글/공백만 남기고 나머지 기호는 제거한 뒤, 공백을 '_'로 치환
// (예: "성남시 건강가정지원센터" → "성남시_건강가정지원센터.png",
//      "동일드방레 (라코스테)" → "동일드방레_라코스테.png")
// alt 텍스트는 원래 표기(공백·괄호 포함)를 그대로 사용합니다.
const toFileName = (name) =>
  name.replace(/[^\w\-가-힣 ]/g, '').trim().replace(/ /g, '_');
const logo = (name) => ({ name, img: LOGO_BASE + toFileName(name) + '.png' });

/* ============================================================
 * 그 외 이미지 경로: C:\Git\newhome\frontend\public\images
 * (자사 로고, 히어로 배경사진, Member Interview 인물사진 5장 × 2버전)
 * 아래 파일들을 public/images 폴더에 넣어 주세요.
 *   - YC_SmartApps_logo.png    (헤더·푸터 공용 자사 로고)
 *   - hero_building.jpg        (히어로 배경 — 사옥 전경)
 *   - thumb_benefit.jpg / thumb_culture.jpg / thumb_office.jpg /
 *     thumb_growth.jpg / thumb_onboarding.jpg   (카드 썸네일, 5장)
 *   - modal_benefit.jpg / modal_culture.jpg / modal_office.jpg /
 *     modal_growth.jpg / modal_onboarding.jpg   (모달 상세사진, 5장 — 썸네일과 다른 이미지입니다)
 * ============================================================ */
const IMAGE_BASE = '/images/';

// 자사 로고(Y&C SmartApps) — 파트너사 92개 로고와 별도 파일입니다.
const LOGO = IMAGE_BASE + 'YC_SmartApps_logo.png';

// 히어로 배경 — 사옥 전경 사진
const HERO_PHOTO = IMAGE_BASE + 'hero_building.jpg';

// ---- Partner Band · 함께한 고객사 (원본 HTML 순서 그대로) ----
const ROW1_LOGOS = [
  logo('HD한국조선해양'),
  logo('HD현대일렉트릭'),
  logo('HYUNDAI POWER SYSTEMS'),
  logo('KCC'),
  logo('HL만도'),
  logo('HD현대삼호'),
  logo('서울아산병원'),
  logo('HD현대중공업'),
  logo('원익머트리얼즈'),
  logo('KCC글라스'),
  logo('홈씨씨인테리어'),
  logo('한국부동산원'),
  logo('삼척시 농업기술센터'),
  logo('식품의약품안전처'),
  logo('효성ITX'),
  logo('넥센'),
  logo('디와이피엔에프'),
  logo('통계개발원'),
  logo('캐럿글로벌'),
  logo('에스엠미디텍'),
  logo('화천군 농업기술센터'),
  logo('동일드방레 (라코스테)'),
  logo('씨티그룹글로벌마켓증권'),
  logo('이엠코리아'),
  logo('인천소방본부'),
  logo('대한무역투자진흥공사'),
  logo('한화자산운용'),
  logo('헌법재판소'),
  logo('포스코'),
  logo('LG생활건강'),
  { ...logo('법무법인 김장리'), style: { height: 40 } },
];

const ROW2_LOGOS = [
  { ...logo('피엔알'), style: { height: 20 } },
  logo('대구교통공사'),
  logo('철원군 보건소'),
  { ...logo('금강레저'), style: { height: 56 } },
  logo('지상작전사령부'),
  logo('쿡메디컬코리아'),
  logo('주택도시보증공사'),
  logo('이수페타시스'),
  logo('한국사회보장정보원'),
  logo('한국씨티은행'),
  logo('국가철도공단'),
  logo('달성군시설관리공단'),
  logo('국립전파연구원'),
  logo('한국거래소'),
  logo('투썸플레이스'),
  logo('중소벤처기업부'),
  { ...logo('LS메탈'), style: { height: 20 } },
  logo('플랜텍'),
  logo('행정안전부'),
  logo('서울지방식품의약품안전청'),
  logo('경찰청'),
  logo('후쏘코리아'),
  logo('산업연구원'),
  logo('한국의약품안전관리원'),
  logo('용인문화재단'),
  logo('방송통신위원회'),
  logo('일동제약'),
  logo('Kt엔지니어링'),
  logo('서울대학교병원'),
  logo('국군의무사령부'),
  logo('LG전자'),
  logo('SK플래닛'),
  logo('HL디앤아이한라'),
  logo('코리아오토글라스'),
];

const ROW3_LOGOS = [
  logo('방위사업청'),
  logo('통계청'),
  logo('대한민국 육군'),
  logo('국토연구원'),
  logo('대한장애인체육회'),
  logo('한국자산관리공사'),
  logo('타나베파마코리아'),
  logo('한국공항공사'),
  logo('고려대학교의료원'),
  logo('강원특별자치도'),
  logo('질병관리청'),
  logo('예금보험공사'),
  logo('식품의약품안전평가원'),
  logo('구리시청'),
  logo('교육부'),
  logo('알바레즈앤마살코리아'),
  logo('육군종합군수학교'),
  logo('일진그룹'),
  logo('강원특별자치도 소방본부'),
  logo('대한민국 해병대'),
  logo('용인특례시'),
  { ...logo('TG삼보'), style: { height: 20 } },
  logo('TKG태광'),
  logo('한화투자증권'),
  logo('성남시 건강가정지원센터'),
  logo('육군정보통신학교'),
  logo('주한호주대사관'),
  logo('양양군 농업기술센터'),
  logo('강원도동해안산불방지센터'),
  logo('원주시 농업기술센터'),
  logo('CNP Laboratory'),
];

// ---- 히어로 메시지: 한 줄씩 등장 ----
const HERO_LINES = [
  { variant: 'normal', node: <>14년 전 열 평이 채 안 되는 사무실을 빌려 <em>1인 회사</em>로 시작했습니다.</> },
  { variant: 'motto', node: <><span className="quote">&ldquo;사람과 행복&rdquo;</span>이라고 사훈을 만들었습니다.</> },
  { variant: 'normal', node: <>경영의 중심은 결국 <em>사람</em>이며, 사람이 행복하면 돈은 얼마든지 벌 수 있을 거라고 생각했습니다.</> },
  { variant: 'normal', node: <>대기업에서 다양한 경험을 쌓은, 자기의 뜻을 세상에 펼치고 싶은 리더들이 모였습니다.</> },
  { variant: 'normal', node: <>우리 사회가 미처 알아보고 있지 못하고 있던 <em>성장 잠재력이 커다란 사람들</em>을 부지런히 찾아 모으고, 실력을 쌓아왔습니다.</> },
  { variant: 'normal', nowrap: true, node: <>경영컨설팅, SI/SM, Total ITO, 영상회의 솔루션 공급 등의 사업영역에서 많은 고객들에게 서비스를 제공하며 관련 기술을 축적해 왔습니다.</> },
  { variant: 'normal', node: <>다양한 산업 내 SCM과 관련된 비즈니스 컨셉에 대한 이해도를 높여왔습니다.</> },
  { variant: 'normal', node: <>이제 그간 모아 온 사람, 기술, 경험, 비즈니스 도메인 knowledge를 활용하여<br /><span className="peach">AX Solution Company</span>로 거듭나려 합니다.</> },
  { variant: 'normal', withMini: true, node: <>그동안 우리가 경험해온 다양한 행복이<br /><em>우리 고객의 행복</em>으로 이어질 수 있도록 최선을 다하려 합니다.</> },
];

// ---- 연구 분야 (Growth) ----
const RESEARCH_CARDS = [
  { num: '01', title: <>AI 기반 인테리어 <br />소프트웨어 개발</>, desc: '인테리어 사업자의 효율과 수익성을 제고하는 AI 에이전트 기반 인테리어 플랫폼을 구축하고 운영합니다.' },
  { num: '02', title: <>AI 솔루션 사업화 <br /> <br /></>, desc: 'SI/SM을 통해 확보한 비즈니스 도메인의 이해에 AI를 접목하여 솔루션화합니다.' },
  { num: '03', title: <>AI 기반 코딩 방법론 솔루션화  <br /> <br /></>, desc: 'AI 기반 코딩 방법론을 정립하고, 이를 상용화하여 AX 중심의 시스템 구축 환경을 만들어갑니다.' },
];

// ---- 현장의 목소리 (Voice · 현재 디자인상 숨김 섹션 — HTML과 동일하게 유지) ----
const VOICE_CARDS = [
  {
    org: '지능형SW연구소',
    q: 'Q. 연구소가 일하는 방식에서 가장 중요한 원칙은?',
    a: '사업자에게 새로운 방식을 배우라고 요구하지 않는 것. 지금 방식 그대로, AI가 뒤에서 정리·연결·조언합니다.',
    avatar: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="6" width="12" height="12" rx="2" /><path d="M9 9h6v6H9z" />
        <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" />
      </svg>
    ),
  },
  {
    org: 'AX사업본부',
    q: 'Q. AI를 통해 만들어낸 효율의 실체는?',
    a: '물류 작업 시간 20~30% 단축, 보고서 작성 시간 50%↓, 요구사항 정리 30~40%↓ 등 데이터 기반 운영 전환.',
    avatar: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="3" y="3" width="7.5" height="7.5" rx="1.8" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.8" />
        <path d="M10.5 6.75h4.75a2 2 0 0 1 2 2v4.75" /><path d="M13.5 17.25H8.75a2 2 0 0 1-2-2v-4.75" />
      </svg>
    ),
  },
  {
    org: '기업사업본부',
    q: 'Q. 본부가 정의하는 비즈니스 지향점은?',
    a: '고객의 산업을 넘어 본질을 이해하고, 디지털 기술로 성장을 함께 만드는 Digital Transformation Partner.',
    avatar: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 21V6l7-3 7 3v15" /><path d="M2 21h20" />
        <path d="M8 9h.01M11 9h.01M8 12h.01M11 12h.01M8 15h.01M11 15h.01" /><path d="M15 21v-9h3v9" />
      </svg>
    ),
  },
  {
    org: '영상회의솔루션',
    q: 'Q. 이 유닛이 추구하는 핵심 가치는?',
    a: '사람과 사람, 공간과 공간을 연결. 구축 이후에도 지속적인 관계를 이어가는 서비스 마인드.',
    avatar: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2.5" y="5" width="13" height="10" rx="1.8" /><path d="M15.5 9l6-3v9l-6-3z" />
        <path d="M6 19h6" /><path d="M9 15v4" />
      </svg>
    ),
  },
];

// ---- 현장의 목소리 모달 상세 데이터 ----
const VOICE = [
  { org: '지능형SW연구소', badge: 'R&D · AI 플랫폼', q: 'Q. 연구소가 일하는 방식에서 가장 중요하게 여기는 원칙은 무엇인가요?', a: ['저희는 사업자에게 새로운 방식을 배우라고 요구하지 않는 것을 상업의 출발점으로 삼습니다. 지금 하던 방식 그대로 쓰면 AI가 뒤에서 정리·연결·조언하는 구조이기 때문에, 도입 장벽을 낮추고 신뢰를 먼저 쌓은 뒤 그 위에 수익을 만듭니다.', '이 원칙은 저희가 정의하는 효율과도 맞닿아 있습니다. 현장 사진 한 장이 공사일지·고객 리포트·포트폴리오에 동시에 연결되고, 견적서 하나가 스케줄·발주·계약서로 자동 전환됩니다. 같은 정보를 네 곳에 따로 입력하던 방식에서, 한 번의 입력으로 전체가 연동되는 방식으로 — 저희에게 효율은 속도가 아니라 입력 대비 산출의 배수입니다.', '그래서 저희가 택한 방법론은 특정 고객을 위한 맞춤 개발이 아니라, 반복 판매 가능한 표준 에이전트를 만들고 사업자가 필요한 것을 골라 쓰게 하는 구조입니다. 이 방법론이 가능했던 이유는 경험에 있습니다. 오전엔 카톡 수십 개를 뒤지고, 현장에선 수첩 메모로 자재를 계산하고, 저녁엔 엑셀 견적서를 다시 만드는 사장님들의 하루를 직접 관찰하며, 20년 경력자도 매 현장마다 반복하는 비효율을 낱낱이 파악했습니다. 그 문제 정의력이 지금의 10개 에이전트 설계로 이어졌습니다.'] },
  { org: 'AX사업본부', badge: 'AX · 제조·유통 디지털 전환', q: 'Q. AI를 통해 만들어낸 효율의 실체와 그 방법론은 무엇인가요?', a: ['AI를 활용해 제조·유통 현장의 데이터 수집·분석·의사결정 과정을 자동화·고도화함으로써, 기존 수작업 중심의 업무 방식을 데이터 기반 운영 체계로 전환했습니다.', '재고 배치 최적화와 출고 동선 분석으로 물류 작업 시간을 20~30% 단축했고, 생산 실적 및 품질 데이터 분석 자동화로 보고서 작성 시간을 50% 이상 절감했습니다. 동시에 요구사항 정리·설계 문서 작성 시간은 평균 30~40%, 테스트 시나리오 생성과 오류 분석 시간은 50% 이상 줄었습니다.', "이 과정에서 얻은 경험은 명확합니다 — 효율은 '더 빨리'가 아니라 '담당자의 경험과 수작업에 의존하던 판단을 표준화·자동화하는 것'이라는 사실입니다. 운영(SM) 과정의 반복적 장애 분석과 원인 추적까지 AI가 지원하면서, 문제 해결의 속도와 정확도가 함께 향상되었습니다."] },
  { org: '기업사업본부', badge: 'Enterprise IT · DT Partner', q: 'Q. 정의하는 비즈니스의 지향점은 무엇이며, 이를 실현하기 위해 어떤 역량에 집중하고 있습니까?', a: ['기업사업본부는 고객의 산업을 넘어 비즈니스의 본질을 먼저 깊이 이해하고, 디지털 기술로 실질적인 성장을 함께 만드는 \u2018Digital Transformation Partner\u2019라는 상업의 가치관을 실천하고 있습니다.', '우리는 이를 말로만 앞세우지 않고, HD현대중공업그룹의 대규모 Enterprise 핵심 시스템들을 성공적으로 구축하고 안정적으로 운영해 온 검증된 경험과 독보적인 노하우로 증명합니다. 복잡하고 까다로운 대기업 환경에서 이미 철저하게 검증된 이 실무 자산은, 어떤 비즈니스 환경에서도 새로운 가치를 즉각적으로 만들어내는 우리만의 강력한 실행력입니다.', '이러한 경험을 바탕으로, 우리는 구축과 운영의 경계를 허물고 프로세스의 시작부터 끝까지 책임지는 통합 수행 방법론을 적용하고 있습니다. 구매 및 PLM 등 기업의 핵심 업무 영역에 AI, Cloud, Data, Automation 기술을 유연하게 내재화하고, 구매 플랫폼 전문 기업과의 전략적 솔루션 협업을 통해 비즈니스 운영 효율성을 극대화합니다.', '단순한 IT 공급자를 넘어, 다양한 산업의 고객이 직면한 문제를 지금 당장 해결하고 데이터 기반의 혁신을 리드하는 가장 신뢰받는 엔터프라이즈 IT 전문 조직으로 활약하고 있습니다.'] },
  { org: '영상회의솔루션', badge: 'Collaboration · 연결을 통한 협업의 가치', q: 'Q. 이 유닛이 추구하는 핵심 가치는 무엇인가요? (산업의 가치관 — “연결을 통한 협업의 가치”)', a: ['사람과 사람, 공간과 공간을 연결하는 것을 기술의 본질로 봅니다. 단순히 음성을 전달하는 도구가 아니라, 물리적으로 떨어진 사람들이 마치 같은 공간에 있는 것처럼 프레젠테이션을 하고 정보를 실시간으로 주고받을 수 있게 하는 것이 화상회의 시스템의 가치입니다. 공공기관, 민간기업, 글로벌 기업 등 협업이 필수적인 조직일수록 이 연결의 가치는 더 커집니다. 여기에 더해, 구축 이후에도 고객과 일회성이 아닌 지속적인 관계를 이어간다는 서비스 마인드를 산업의 태도로 삼고 있습니다.'] },
];

// ---- 우리가 지향하는 것 / 하지 않는 것 (Principles) ----
const PRINCIPLES = [
  {
    tile: 'S', label: '성실 · Sincerity',
    value: '진정성과 집중력으로 주어진 역할을 완수하는 인재를 육성함',
    headline: "'될 것 같습니다'라고 말하지 않습니다.",
    desc: '확신 없는 약속은 하지 않습니다. 하기로 한 일은 확실하게 해냅니다.',
  },
  {
    tile: 'M', label: '상호존중 · Mutual Respect',
    value: '권위주의를 지양하고 상호 배려와 격려를 통하여 성과를 창출할 수 있는 환경을 조성함',
    headline: "무조건 '네'라고 답하지 않습니다.",
    desc: '상호 배려와 격려 속에 오가는 다른 의견이, 성과를 만듭니다.',
  },
  {
    tile: 'A', label: '열망 · Aspiration',
    value: '일을 통하여 능동적으로 개인의 발전을 추구하며 성장해 나아가는 문화를 정착시킴',
    headline: "'이 정도면 충분합니다'에서 멈추지 않습니다.",
    desc: '몰입한 시간은 소모가 아닙니다. 어제보다 나은 자신으로 성장하게 합니다.',
  },
  {
    tile: 'R', label: '책임감 · Responsibility',
    value: '주어진 목표 및 성과에 책임을 지도록 하며 성취에 대한 합리적이고 공정한 보상을 시행함',
    headline: '책임을 미루지도, 보상을 미루지도 않습니다.',
    desc: '구성원은 결과로 책임지고, 회사는 보상으로 책임집니다.',
  },
  {
    tile: 'T', label: '연대감 · Togetherness',
    value: '조직공동체의 전략 및 가치를 공유하고 구성원 상호간 협력을 기반으로 고객, 구성원, 회사의 동반성장을 실현함',
    headline: "'한 명의 영웅'에게는 의존하지 않습니다.",
    desc: '장면마다 주인공은 달라도, 무대를 세우는 건 언제나 모두의 역할입니다.',
  },
];

// ---- Work With Us (인재상 4카드) ----
const CAREER_COLS = [
  {
    num: '01', title: <>성장의 크기에<br />공감하는 사람</>,
    desc: <>회사가 제시하는 성장의 크기에 <br />함께 설레고 공감합니다.</>,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" />
      </svg>
    ),
  },
  {
    num: '02', title: <>생각을 <br />말하는 사람</>,
    desc: <>생각을 나누면 상식을 공유합니다.<br /> 상식이 결과가 됩니다.</>,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 10h8M8 14h5" /><path d="M4 5h16v11H9l-4 4V16H4z" />
      </svg>
    ),
  },
  {
    num: '03', title: <>더 나은 것을<br />찾는 사람</>,
    desc: <>어제 방식은 당연히 <br />오늘의 최선이 아닙니다.</>,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9.5 15a5 5 0 1 1 5 0c-.8.5-1 1-1 2v.5h-3V17c0-1-.2-1.5-1-2z" /><path d="M10 21h4" />
      </svg>
    ),
  },
  {
    num: '04', title: <>방법을 찾는 데<br />지치지 않는 사람</>,
    desc: <>안 되는 이유 대신, <br />되는 방법을 끝까지 찾습니다.</>,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="19" r="2" /><circle cx="18" cy="5" r="2" /><path d="M8 19h7a4 4 0 0 0 0-8H9a4 4 0 0 1 0-8h7" />
      </svg>
    ),
  },
];

// ---- 채용 프로세스 ----
const PROCESS_STEPS = [
  { n: 1, title: '서류 전형', sub: '이력서 · 포트폴리오', desc: '형식보다 이야기가 중요합니다' },
  { n: 2, title: '1차 면접', sub: '직무 역량 확인', desc: '주어질 일을 좋아하는지 보는 시간' },
  { n: 3, title: '2차 면접', sub: '임원과의 대화', desc: '서로를 알아가는 시간' },
  { n: 4, title: '최종 합격', sub: '처우 협의', desc: '이제 같이 시작합니다', final: true },
];

// ---- Member Interview 카드 (슬라이더) ----
// thumbPos: 원본 HTML의 인라인 배경 위치값 그대로 ("center 20%")
const PEOPLE_CARDS = [
  { title: <>분기마다 채워지는<br />나만의 성장 예산 사용법</>, desc: <>콘서트부터 자격증 두 개까지 — 자기계발비<br /> 200% 활용기.</>, author: 'AX사업본부 4년차 매니저', thumb: IMAGE_BASE + 'thumb_benefit.jpg', thumbPos: 'center 20%' },
  { title: <>직급 대신 매니저,<br />회의가 달라졌습니다</>, desc: <>묻고 답하는 게 가벼워지면 일도 그만큼 빨리<br />풀립니다.</>, author: 'AX사업본부 4년차 매니저', thumb: IMAGE_BASE + 'thumb_culture.jpg', thumbPos: 'center 20%' },
  { title: <>낮잠과 탁구가 있는 사무실,<br />신사옥 이야기</>, desc: <>일도 휴식도 확실하게 — 복합 힐링 공간이 된<br />새 사옥 투어.</>, author: '경영관리본부 2년차 매니저', thumb: IMAGE_BASE + 'thumb_office.jpg', thumbPos: 'center 20%' },
  { title: <>퍼블리셔 15년,<br />AI와 함께 개발자가 되다</>, desc: <>AI가 열어준 두 번째 커리어 — 경력 전환의<br /> 기록.</>, author: 'AX사업본부 6년차 매니저', thumb: IMAGE_BASE + 'thumb_growth.jpg', thumbPos: 'center' },
  { title: <>신입의 2개월,<br />&ldquo;멘토가 있어서 달랐습니다&rdquo;</>, desc: <>대표님과의 점심부터 멘토링까지, 온보딩의<br />기록.</>, author: 'AX사업본부 3년차 매니저', thumb: IMAGE_BASE + 'thumb_onboarding.jpg', thumbPos: 'center 20%' },
];

// 스토리 모달에서 보여줄 상세 사진 (썸네일과는 다른 이미지입니다)
const STORY_HEROES = [
  IMAGE_BASE + 'modal_benefit.jpg',
  IMAGE_BASE + 'modal_culture.jpg',
  IMAGE_BASE + 'modal_office.jpg',
  IMAGE_BASE + 'modal_growth.jpg',
  IMAGE_BASE + 'modal_onboarding.jpg',
];

// ---- 직원 인터뷰 상세 (모달) ----
const STORIES = [
  {
    badge: 'Benefit',
    title: '분기마다 채워지는 나만의 성장 예산 사용법',
    who: 'AX사업본부 제조AX사업 Unit 4년차 매니저 · 전사 ERP 구매 모듈 운영',
    qa: [
      { q: 'Q. 어떤 일을 하고 계신가요?',
        a: ['<p>전사 ERP 구매 모듈을 담당하고 있어요. 회사가 물건을 구매하는 전 과정을 시스템으로 처리하는 과정을 운영하는 일이에요. 문제가 생기면 즉시 원인을 파악하고 조치하는 것을 가장 우선시하고, 요청에 따라 신규 기능을 추가하거나 기존 기능을 개선하는 일도 하고 있습니다.</p>'] },
      { q: 'Q. 자기계발비, 실제로 어떻게 활용하고 계신가요?',
        a: ['<p>콘서트나 영화 관람을 자주 하는 편이라 문화생활에 가장 많이 활용하고 있어요. 올해도 벌써 콘서트 한 번, 영화제 한 번을 다녀왔는데, 일하는 중간에 이런 시간이 있다는 게 생각보다 큰 활력이 돼요.</p>',
           '<p>업무적인 활용도 했는데요. 자격증 교재 구입과 시험 응시료로 써서 작년에 정보처리기사와 SQLD를 취득했습니다. 회사 지원이 있으니까 도전 문턱이 확실히 낮아지더라고요.</p>'] },
      { q: 'Q. 그 활용을 통해 얻은 변화나 성장이 있다면?',
        a: ['<p>공연을 보고 온 날이면 일상에 기대감이 생겨서 에너지가 채워지는 느낌이에요. 자격증 공부할 때도 비용 부담 없이 집중할 수 있었던 덕분에 작년에 두 개를 한 번에 딸 수 있었던 것 같아요.</p>',
           '<p>취미나 공부를 회사가 직접 응원해준다는 게 말로 듣는 것보다 훨씬 크게 와닿아서, 자연스럽게 회사에 대한 애정도 생기더라고요.</p>'] },
      { q: 'Q. 활용을 고민하는 동료들에게 팁을 준다면?',
        a: ['<p>본인의 문화생활에 쓰시거나 스마트워치처럼 업무에 도움이 되는 장비를 구입해 보세요. 저도 하나 샀는데 업무 알림을 놓치는 일이 눈에 띄게 줄었어요. 팀에서도 스마트워치를 구입하시는 분들이 많더라고요. 무엇보다 건강이 우선이니까, 운동이나 건강관리에 쓰시는 것도 적극 추천드립니다.</p>'] },
    ],
  },
  {
    badge: 'Culture',
    title: '직급 대신 매니저, 회의가 달라졌습니다',
    who: 'AX사업본부 유통AX사업 Unit 4년차 매니저 · MDM·EAI·전자결재·ERP 개발',
    qa: [
      { q: 'Q. 어떤 일을 하고 계신가요?',
        a: ['<p>MDM, EAI, 전자결재, ERP 같은 시스템의 화면 개선과 기능 개발을 담당하고 있어요. 간단히 말하면 회사 안 여러 시스템들끼리 데이터를 잘 주고받게 만드는 일이에요. 유지보수도 하고, 필요하면 신규 개발이나 다른 시스템과의 데이터 연동 작업도 같이 하고 있습니다.</p>'] },
      { q: 'Q. 수평적 소통 문화, 실제로 어떻게 경험하고 계신가요?',
        a: ['<p>저희 조직은 직급 없이 다 매니저로 불러요. 처음엔 그냥 호칭만 바뀐 건 줄 알았는데, 회의에 들어가 보니 진짜 다르더라고요. 누가 먼저 말해야 한다는 순서 같은 것 없이, 생각나면 바로 말합니다. 저도 입사하고 처음엔 좀 눈치를 봤는데, 어느 순간부터 그냥 편하게 묻고 말하게 됐어요.</p>'] },
      { q: 'Q. 그 경험을 통해 느낀 변화가 있다면?',
        a: ['<p>제일 크게 바뀐 건 일할 때 신경 쓰는 포인트예요. 사람보다 업무 자체를 먼저 보게 됐어요. 예전 같으면 막혔을 때 \u2018이거 물어봐도 되나\u2019 고민했을 텐데, 지금은 담당자만 확인하면 바로 연락해요.</p>',
           '<p>실제로 다른 시스템 작업을 하다 막혔을 때 담당 매니저님께 메시지를 보냈더니 그날 바로 답을 받았어요. 묻고 답하는 게 가벼워지니까 일도 그만큼 빨리 풀리는 느낌입니다.</p>'] },
      { q: 'Q. 이 문화를 추천한다면 꼭 말해주고 싶은 점은?',
        a: ['<p>처음엔 직급 신경 안 쓰고 말 거는 게 진짜 어색해요. 저도 그랬어요. 근데 막상 한두 번 해보면 별거 아니고, 그 뒤로는 일이 훨씬 편해져요. 혼자 끙끙대지 말고 편하게 물어보세요. 그게 본인한테도, 일이 풀리는 데도 훨씬 낫습니다.</p>'] },
    ],
  },
  {
    badge: 'Office',
    title: '낮잠과 탁구가 있는 사무실, 신사옥 이야기',
    who: '경영관리본부 2년차 매니저 · 사내 지원·운영 총괄',
    qa: [
      { q: 'Q. 어떤 일을 하고 계신가요?',
        a: ['<p>임직원분들이 안정적인 환경에서 업무에 몰입할 수 있도록 회사의 살림과 지원을 전반적으로 총괄하는 업무를 맡고 있습니다.</p>'] },
      { q: 'Q. 신사옥에서 가장 좋아하는 공간은 어디인가요?',
        a: ['<p>새로 생긴 여성휴게실과 다채로운 리프레시 공간을 가장 좋아합니다. 특히 여성휴게실 안의 1인용 리클라이너 소파 두 대는 제 최애 스폿인데요, 점심 먹고 잠깐 달콤한 낮잠을 자기에 최고예요!</p>',
           '<p>넓은 휴게 공간에는 큰 소파와 작은 소파가 있어 편하게 누워 쉴 수도 있고, 정기 구독하는 네스프레소 캡슐 커피를 마시며 도란도란 담소를 나누기도 합니다. 매달 들어오는 패션·IT·포브스·인테리어 잡지 4종을 보며 트렌드를 읽는 휴식도 정말 좋습니다.</p>'] },
      { q: 'Q. 이전 후 팀 분위기에 달라진 점이 있다면?',
        a: ['<p>점심시간과 휴식시간의 풍경이 정말 에너제틱하게 바뀌었습니다. 탁구대 덕분에 점심 먹고 내기 탁구를 치는 직원들이 많아졌고, 턱걸이 기구 앞에서는 운동에 진심인 직원들의 건강한 모습도 자주 보입니다. 머리를 식히고 싶을 땐 닌텐도 스위치로 게임을 즐기기도 해요.</p>',
           '<p>\u2018쉴 때는 확실하고 건강하게 즐기는 문화\u2019가 자연스럽게 자리 잡으면서, 부서 간 장벽 없이 함께 어우러지는 활기찬 분위기가 되었습니다.</p>'] },
      { q: 'Q. 신사옥을 한 문장으로 소개한다면?',
        a: ['<p>“업무 효율을 높여주는 최고의 일터이자, 꿀맛 같은 휴식과 건강한 에너지가 가득 충전되는 복합 힐링 공간!”</p>',
           '<p>우리 회사는 열심히 일하는 것만큼 \u2018잘 쉬는 것\u2019의 가치를 아는 곳입니다. 업무 스트레스를 회사 안에서 건강하게 해소할 수 있는 멋진 신사옥이 여러분을 기다리고 있으니 주저 말고 지원해 주세요!</p>'] },
    ],
  },
  {
    badge: 'Growth',
    title: '퍼블리셔 15년, AI와 함께 개발자가 되다',
    who: 'AX사업본부 유통AX사업 Unit 6년차 매니저 · 프론트엔드 개발',
    qa: [
      { q: 'Q. 지금 맡고 있는 업무를 소개해주세요.',
        a: ['<p>유통AX사업 유닛에서 프론트엔드 개발을 담당하고 있습니다. 최근에는 AI 기반 WMS(창고관리시스템) 프로젝트를 진행하며, AI 코딩 도구를 활용해 개발 구조를 이해하고 프론트엔드와 백엔드가 어떻게 연결되는지까지 확장해가고 있습니다.</p>',
           '<p>단순히 코드를 작성하는 것보다 “이 화면이 어떤 데이터와 연결되고, 어떤 업무 흐름 안에서 쓰이는가”를 파악하는 데 집중하고 있습니다.</p>'] },
      { q: 'Q. 전환을 결심한 계기가 궁금해요.',
        a: ['<p>프론트 퍼블리싱을 15년 이상 해왔습니다. 개발자로 전환하고 싶다는 생각은 예전부터 있었지만, 경력이 쌓일수록 현실적인 간극이 생겼습니다. 신입처럼 다시 시작하기에는 쌓아온 경력이 있었고, 바로 인정받기에는 실무 개발 경험이 부족했으니까요.</p>',
           '<p>그런데 AI가 등장하면서 그 간극을 다른 방식으로 메울 수 있겠다는 생각이 들었습니다. 특히 회사가 AI를 실제 업무에 활용하는 방향으로 움직이고 있다는 점이 큰 계기가 됐습니다. 혼자 다시 시작하는 것이 아니라, 회사의 AI 활용 흐름 안에서 기존 경험과 새로운 개발 역량을 연결해볼 수 있겠다고 생각했습니다.</p>'] },
      { q: 'Q. 가장 막막했던 순간과 푸는 방법은?',
        a: ['<p>개발자들이 나누는 대화의 맥락을 따라가지 못할 때였습니다. 무엇을 모르는지조차 모르는 상태라 질문하는 것도 쉽지 않았습니다.</p>',
           '<p>그때 도움이 된 것이 AI였습니다. 너무 기본적인 질문이라 묻기 어려웠던 것도 AI에게는 부담 없이 물어볼 수 있었어요. 동료들에게는 실무 맥락을 묻고, AI에게는 기초 개념과 구조를 물었습니다. 그렇게 하다 보니 흩어져 있던 단어들이 점점 하나의 흐름으로 연결되기 시작했습니다.</p>'] },
      { q: 'Q. 비슷한 고민을 하는 분들께 한마디 해주신다면?',
        a: ['<p>시작해보니 처음부터 모든 것을 알고 있어야 하는 건 아니었습니다. 모르는 것을 하나씩 묻고, 정리하고, 제 업무와 연결해보는 과정 안에서 조금씩 길이 보였습니다.</p>',
           '<p>저에게는 물러설 곳이 없다는 절박함이 시작의 이유가 되었지만, 지금은 그 절박함이 조금씩 방향성으로 바뀌고 있습니다. 앞으로도 계속 배우면서, 해온 경험과 새롭게 익히는 기술을 연결해가고 싶습니다.</p>'] },
    ],
  },
  {
    badge: 'Onboarding',
    title: '신입의 2개월, “멘토가 있어서 달랐습니다”',
    who: 'AX사업본부 제조AX사업 Unit 3년차 매니저 · ERP 생산/물류 모듈 운영',
    qa: [
      { q: 'Q. 어떤 일을 하고 계신가요?',
        a: ['<p>ERP 시스템에서 생산/물류 모듈을 담당하고 있어요. 물품이 생산되면서부터 고객에게 도착하기까지의 과정 속에서 시스템이 원활하게 운영되도록 개발과 유지보수를 진행하고 있습니다!</p>'] },
      { q: 'Q. 온보딩 과정은 실제로 어떻게 진행되나요?',
        a: ['<p>저희 회사 온보딩은 크게 입사 첫 이틀과, 그 이후 2달간의 교육 기간으로 나뉘어요. 첫날은 회사 투어부터 시작해서 회사 소개를 듣고 근로계약서를 작성했어요. 점심은 대표님과 함께했고, 오후엔 개인 장비를 받아 세팅하는 시간이 있었는데 그게 꽤 설레더라고요.</p>',
           `<table class="ob-table"><thead><tr><th>시간</th><th>Day 1</th><th>Day 2</th></tr></thead><tbody>
<tr><td>09:00 – 10:00</td><td>회사 투어 👀</td><td>역량 테스트 📝</td></tr>
<tr><td>10:00 – 12:00</td><td>회사 소개 · 근로계약서 작성 🎉</td><td>회사 업무 소개 🔍</td></tr>
<tr><td>12:00 – 13:00</td><td>대표님이랑 점심 식사 🍽️</td><td>본부장님이랑 점심 식사 🍽️</td></tr>
<tr><td>13:00 – 14:00</td><td>휴식 😴</td><td>휴식 😴</td></tr>
<tr><td>14:00 – 17:00</td><td>노트북 세팅 💻</td><td>Unit별 업무 소개 · 역량 테스트 해설 · Wrap-up</td></tr>
<tr><td>17:00 – 18:00</td><td>Wrap-up 💬</td><td>Peer Group Icebreaking ✨</td></tr>
</tbody></table>`,
           '<p>이틀이 지나면 바로 실무에 투입되는 게 아니라 2달 동안의 교육 기간이 이어져요. 교육 담당자분이 직접 수업을 해주시고 매번 과제도 나왔는데, 이전 기수 선배들이 돌아가면서 과제를 봐주셨어요. 혼자 공부하는 느낌이 아니라 같이 배워가는 분위기여서, 2달이 지나고 나니 자연스럽게 회사에 녹아들 수 있었어요.</p>'] },
      { q: 'Q. 가장 기억에 남는 멘토링 에피소드는?',
        a: ['<p>교육 기간과는 별개로 회사 생활 전반을 도와줄 멘토를 한 명씩 배정해 주는데요. 저와 같은 부산 출신 멘토님으로 배정해 주셔서, 그것만으로도 마음이 한결 편해졌던 기억이 있어요.</p>',
           '<p>입사하면서 처음 서울 생활을 시작해 아무것도 모른 채 회사 근처에 자취방을 구했는데 월세가 130 가까이 나왔거든요. 그 얘기를 들으시고는 회사 다니기 괜찮으면서 월세 부담은 훨씬 덜한 동네들을 추천해 주셨어요. 타지 생활의 디테일까지 챙겨주셔서, 의지할 수 있는 분이 생겼다는 느낌을 처음 받았던 순간이었어요.</p>'] },
      { q: 'Q. 새로 입사하는 분들에게 이 제도가 어떤 도움이 될까요?',
        a: ['<p>입사 초반엔 누구한테 물어봐야 할지 모르겠는 애매한 순간들이 생각보다 많이 생겨요. 그럴 때 멘토가 있다는 것만으로도 심리적으로 굉장히 편했어요. 출신 지역까지 고려해 배정해 주신다는 걸 알고 나니, 형식적인 제도가 아니라 한 사람 한 사람의 상황을 세심하게 들여다보고 매칭해 주신다는 게 느껴지더라고요.</p>',
           '<p>새로 입사하시는 분들도 궁금한 게 생기면 멘토님께 편하게 물어보셨으면 좋겠어요. 혼자 해결하려고 끙끙대는 시간이 줄어드는 것만으로도 회사에 적응하는 속도가 확실히 달라질 거예요.</p>'] },
    ],
  },
];

// ---- 고객이 보내온 메시지 (TESTIMONIALS) ----
const TESTIMONIALS = [
  {
    text: '프로젝트 기간 내내 우리 업무를 우리보다 더 깊이 이해하려는 모습이 인상적이었습니다. 시스템 오픈 이후에도 먼저 연락해 개선점을 챙겨주는 파트너였습니다.',
    from: '— 제조 대기업 시스템 운영 담당자 (예시 문구)',
  },
  {
    text: '장애가 생기면 밤낮없이 원인을 찾아 해결해 주셨습니다. 오랜 기간 믿고 맡길 수 있었던 이유입니다.',
    from: '— 공공기관 정보화 담당자 (예시 문구)',
  },
];

// ---- 복리후생 ----
const BENEFITS = [
  {
    title: '자기계발', sub: '분기마다 채워지는 성장 예산',
    items: ['분기별 자기계발비 지원 (분기 이월 가능)', '학원 · 도서 · 자격증 · IT장비 구입', '공연 · 전시 등 문화 체험이나 건강관리에 활용'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v15H5.5A1.5 1.5 0 0 0 4 20.5z" />
        <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v15h5.5a1.5 1.5 0 0 1 1.5 1.5z" />
      </svg>
    ),
  },
  {
    title: '건강 & 경조', sub: '몸도, 마음도, 가족도',
    items: ['연 1회 기업검진 지원', '결혼 비용지원 및 휴가 제공', '경조사 지원'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20s-7-4.3-9.3-9C1.2 8 2.6 5 5.5 5 7.5 5 9 6.3 12 9c3-2.7 4.5-4 6.5-4 2.9 0 4.3 3 2.8 6-2.3 4.7-9.3 9-9.3 9z" />
      </svg>
    ),
  },
  {
    title: '재무 & 안정', sub: '지금도, 나중도 든든하게',
    items: ['DC형 퇴직연금 매년 적립', '매년 3월 연봉 정기 인상', '중식대 지원', '지방 근무자 주거비 지원'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" /><path d="M7 15h4" />
      </svg>
    ),
  },
  {
    title: '열정 & 보답', sub: '함께 나누는 행복',
    items: ['연말 실적 성과급 지급', '장기근속자 포상(3 ~ 20년)', '우수사원 포상', '하계 휴가비 지급', '명절 · 창립기념일 · 생일 상품권 선물'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.4-.5-2-1-3-1.1-2.1-.2-4.1 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.2.4-2.3 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
      </svg>
    ),
  },
  {
    title: '공간 & 문화', sub: '일하고 싶은 공간',
    items: ['자유복장 및 연장 · 휴일근무 지양', '연차, 반차, 대체휴가, 산전 · 후 휴가, 육아휴직, 포상휴가', '여성 전용 휴게실', '지하 라운지 (탁구 · 게임 · 각종 편의시설)', '월간 과자 구독 + 커피머신'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21V9l9-6 9 6v12" /><path d="M9 21v-6h6v6" />
      </svg>
    ),
  },
  {
    title: '상생 & 신뢰', sub: '함께 일하는 파트너까지 든든하게',
    items: ['외주인력 용역비 당월 현금지급', '협력업체 대금 25일 내 현금지급'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 17a5 5 0 0 1-8-4c0-2.8 2.2-5 5-5 .8 0 1.6.2 2.3.6" />
        <path d="M13 7a5 5 0 0 1 8 4c0 2.8-2.2 5-5 5-.8 0-1.6-.2-2.3-.6" />
        <path d="M8 12h8" />
      </svg>
    ),
  },
];

// 이메일: 영문·숫자·일반기호만 허용 (백틱·공백·따옴표 등 차단)
const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
// 연락처: 02·031·010 등 국내 번호 (하이픈 선택)
const PHONE_RE = /^0\d{1,2}[-.\s]?\d{3,4}[-.\s]?\d{4}$/;

// 문의 부서 목록 (문의폼 select 옵션과 담당자 리스트에 공용으로 사용)
// value는 백엔드 naverworks.mail-to-by-department 키와 일치해야 함 (실제 이메일은
// 서버 쪽 매핑에서만 조회 — 클라이언트가 이메일 주소를 직접 지정하지 않도록 코드값만 전송)
const CONTACT_DEPTS = [
  { value: 'ax', label: 'AX사업본부 (이승주 본부장)' },
  { value: 'sw-lab', label: '지능형 SW연구소 (이향호 소장)' },
  { value: 'enterprise', label: '기업사업본부 (김용완 본부장)' },
  { value: 'vc', label: '영상회의 솔루션 (손수영 매니저)' },
  { value: 'mgmt', label: '경영관리본부 (오주연 유닛장)' },
];

function validateField(name, value) {
  const v = (value || '').trim();
  if (name === 'from_name') {
    if (!v) return '이름을 입력해 주세요.';
    if (v.length < 2) return '이름은 2자 이상 입력해 주세요.';
  }
  if (name === 'from_email' && v && !EMAIL_RE.test(v)) {
    return '올바른 이메일 형식으로 입력해 주세요. (예: name@company.com)';
  }
  if (name === 'phone' && v && !PHONE_RE.test(v)) {
    return '연락처 형식을 확인해 주세요. (예: 010-1234-5678)';
  }
  if (name === 'dept') {
    if (!v) return '문의하실 부서를 선택해 주세요.';
  }
  if (name === 'message') {
    if (!v) return '문의 내용을 입력해 주세요.';
    if (v.length < 10) return '문의 내용은 10자 이상 입력해 주세요.';
  }
  return null;
}

const ArrowIcon = ({ size = 13, color = 'white' }) => (
  <svg width={size} height={size} viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.70833 6.5H10.2917" stroke={color} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.5 2.70833L10.2917 6.5L6.5 10.2917" stroke={color} strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightSvg = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" /><path d="M13 6l6 6-6 6" />
  </svg>
);

const ChevronRightSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export default function NewHome() {

  /* ── 모바일 헤더 메뉴 ───────────────────────────── */
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const hideLockUntil = useRef(0);   // 앵커 이동 직후에는 숨기지 않기 위한 잠금
  useEffect(() => {
    let last = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 4);
        const delta = y - last;
        if (y < 90 || Date.now() < hideLockUntil.current) {
          setHideHeader(false);                 // 최상단 근처 · 메뉴 이동 직후
        } else if (delta > 6) {
          setHideHeader(true);                  // 아래로
        } else if (delta < -6) {
          setHideHeader(false);                 // 위로
        }
        last = y;
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    hideLockUntil.current = Date.now() + 900;   // 스무스 스크롤이 끝날 때까지
    setHideHeader(false);
  }, []);

  /* 문의하기 버튼: 모바일에서는 지도·담당자 목록을 건너뛰고 입력폼으로 바로 */
  const goContact = useCallback((e) => {
    setMenuOpen(false);
    hideLockUntil.current = Date.now() + 1400;
    setHideHeader(false);
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    const form = document.getElementById('contact-form');
    if (!form) return;
    e.preventDefault();
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onClickOutside = (e) => { if (!e.target.closest('.gnb')) setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('click', onClickOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClickOutside);
    };
  }, [menuOpen]);
  /* ============ 히어로 메시지 : 한 줄씩 등장 (PPT 날아오기) ============ */
  const [heroLinesOn, setHeroLinesOn] = useState(() => HERO_LINES.map(() => false));

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setHeroLinesOn(HERO_LINES.map(() => true));
      return;
    }
    const timers = HERO_LINES.map((_, i) =>
      setTimeout(() => {
        setHeroLinesOn((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 500 + i * 650)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  /* ============ 스토리 모달 (직원 인터뷰) ============ */
  const [storyIndex, setStoryIndex] = useState(null);
  const openStory = (i) => { setStoryIndex(i); document.body.style.overflow = 'hidden'; };
  const closeStory = useCallback(() => { setStoryIndex(null); document.body.style.overflow = ''; }, []);

  /* ============ 보이스 모달 (본부 인터뷰 · 현재 섹션 숨김 상태) ============ */
  const [voiceIndex, setVoiceIndex] = useState(null);
  const openVoice = (i) => { setVoiceIndex(i); document.body.style.overflow = 'hidden'; };
  const closeVoice = useCallback(() => { setVoiceIndex(null); document.body.style.overflow = ''; }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (storyIndex !== null) closeStory();
      if (voiceIndex !== null) closeVoice();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [storyIndex, voiceIndex, closeStory, closeVoice]);

  /* ============ 직원 인터뷰 카드 슬라이더 ============ */
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(false);

  const updateSliderState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxX = track.scrollWidth - track.clientWidth;
    setPrevDisabled(posRef.current <= 0);
    setNextDisabled(posRef.current >= maxX - 1);
  }, []);

  const goSlider = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('.story-card');
    const step = (card ? card.offsetWidth : 280) + 24;
    const maxX = track.scrollWidth - track.clientWidth;
    posRef.current = Math.max(0, Math.min(maxX, posRef.current + dir * step));
    track.scrollTo({ left: posRef.current, behavior: 'smooth' });
    updateSliderState();
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let syncTimer;
    const onScroll = () => {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(() => { posRef.current = track.scrollLeft; updateSliderState(); }, 150);
    };
    const onResize = () => {
      const maxX = track.scrollWidth - track.clientWidth;
      posRef.current = Math.min(posRef.current, maxX);
      updateSliderState();
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    updateSliderState();
    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      clearTimeout(syncTimer);
    };
  }, [updateSliderState]);

  /* ============ 문의 폼 ============ */
  const [sending, setSending] = useState(false);
  const [okVisible, setOkVisible] = useState(false);
  const [sendError, setSendError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [deptHint, setDeptHint] = useState('');

  const handleDeptChange = (e) => {
    const { value } = e.target;
    const dept = CONTACT_DEPTS.find((d) => d.value === value);
    setDeptHint(dept ? `→ ${dept.label}으로 전달됩니다` : '');
    setFieldErrors((prev) => ({ ...prev, dept: validateField('dept', value) }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const err = validateField(name, value);
    setFieldErrors((prev) => ({ ...prev, [name]: err }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const f = e.target.elements;
    const values = {
      from_name: f['from_name'].value,
      from_email: f['from_email'].value,
      phone: f['phone'].value,
      dept: f['dept'].value,
      message: f['message'].value,
    };

    const errs = {};
    ['from_name', 'from_email', 'phone', 'dept', 'message'].forEach((name) => {
      const err = validateField(name, values[name]);
      if (err) errs[name] = err;
    });
    if (!f['privacy'].checked) {
      errs.privacy = '개인정보 수집·이용에 동의해 주세요.';
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const firstErrKey = Object.keys(errs)[0];
      const idMap = { from_name: 'cf-name', from_email: 'cf-email', phone: 'cf-phone', dept: 'cf-dept', message: 'cf-msg', privacy: 'privacy' };
      document.getElementById(idMap[firstErrKey] || firstErrKey)?.focus();
      return;
    }

    setSending(true);
    setSendError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromName: values.from_name.trim(),
          fromEmail: values.from_email.trim(),
          company: f['company'].value.trim(),
          phone: values.phone.trim(),
          department: values.dept,
          message: values.message.trim(),
        }),
      });
      if (!res.ok) throw new Error('전송 실패: ' + res.status);
      setOkVisible(true);
    } catch (err) {
      console.error('[문의 전송 오류]', err);
      setSendError('전송에 실패하였습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSending(false);
    }
  };

  const story = storyIndex !== null ? STORIES[storyIndex] : null;
  const voice = voiceIndex !== null ? VOICE[voiceIndex] : null;

  return (
    <div className="newhome-page">
      {/* ============ GNB ============ */}
      <header className={`gnb${menuOpen ? ' is-menu-open' : ''}${scrolled ? ' is-scrolled' : ''}${hideHeader && !menuOpen ? ' is-hidden' : ''}`}>
        <a className="gnb__logo" href="#top" onClick={closeMenu}><img src={LOGO} alt="Y&C SmartApps" /></a>

        {/* 모바일 전용 토글 (데스크톱에서는 CSS 로 숨김) */}
        <button
          type="button"
          className="gnb__toggle"
          aria-label={menuOpen ? '메뉴 닫기' : '메뉴 열기'}
          aria-expanded={menuOpen}
          aria-controls="gnb-menu"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>

        {/* 데스크톱에서는 display:contents 라 기존 레이아웃 그대로 */}
        <div className="gnb__menu" id="gnb-menu">
          <nav className="gnb__nav" onClick={closeMenu}>
            <a href="#services">사업영역</a>
            <a href="#growth">성장비전</a>
            <a href="#careers">인재채용</a>
            <a href="#culture">기업문화</a>
            <a href="#contact">오시는길</a>
          </nav>
          <a className="btn-cta" href="#contact" onClick={goContact}>문의하기 <ArrowIcon /></a>
        </div>
      </header>

      {/* ============ Hero (시안1) ============ */}
      <section className="hero">
        <div className="hero__photo" style={{ backgroundImage: `url(${HERO_PHOTO})` }} />
        <div className="hero__overlay" />
        <div className="container">
          <div className="hero__wrap">
            <div>
              <span className="hero__eyebrow">AX 솔루션 기업</span>
              <h1 className="hero__title">걸음은 빠르지 않지만 내실을 우선합니다.<br />함께 오래도록 멀리 가보려 합니다.</h1>
              <p className="hero__desc">비지니스 컨설팅부터 시스템 구축, 운영까지 <br />AX 솔루션으로 완성하는 새로운 삶의 환경</p>

              <div className="hero__message" aria-label="CEO 메시지">
                {HERO_LINES.map((line, i) => {
                  const cls = `hero-line${line.variant === 'motto' ? ' hero-line--motto' : ''}${heroLinesOn[i] ? ' is-on' : ''}`;
                  // 마지막 줄에는 핵심 수치 박스를 우측 하단에 겹쳐 배치 (원본 HTML과 동일)
                  if (line.withMini) {
                    return (
                      <div key={i} className={cls} style={{ marginTop: 20, position: 'relative' }}>
                        <p>{line.node}</p>
                        <div className="hero__mini" aria-label="핵심 수치" style={{ right: 0, bottom: 4 }}>
                          <div><b>14년</b><span>업력</span></div>
                          <div><b>90+</b><span>고객사</span></div>
                          <div><b>200+</b><span>프로젝트 수행</span></div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <p key={i} className={cls} style={line.nowrap ? { whiteSpace: 'nowrap' } : undefined}>
                      {line.node}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {/* 우측 하단 : 신규 사옥 · 핵심 수치 */}
        <div className="hero__side">
          <p className="hero__building">Y&amp;C 신규 사옥 2025년 11월 입주</p>
        </div>
      </section>

      {/* ============ Partner Band · 함께한 고객사 ============ */}
      <section className="partner-band" aria-label="함께한 고객사">
        <div className="partner-marquee" aria-hidden="true">
          <div className="pm-row pm-row--1"><div className="pm-track">
            {[...ROW1_LOGOS, ...ROW1_LOGOS].map((l, i) => (
              <img className="pm-item" key={i} src={l.img} alt={l.name} style={l.style} />
            ))}
          </div></div>
          <div className="pm-row pm-row--2 pm-row--rtl"><div className="pm-track">
            {[...ROW2_LOGOS, ...ROW2_LOGOS].map((l, i) => (
              <img className="pm-item" key={i} src={l.img} alt={l.name} style={l.style} />
            ))}
          </div></div>
          <div className="pm-row pm-row--3"><div className="pm-track">
            {[...ROW3_LOGOS, ...ROW3_LOGOS].map((l, i) => (
              <img className="pm-item" key={i} src={l.img} alt={l.name} style={l.style} />
            ))}
          </div></div>
        </div>
      </section>

      {/* ============ Solutions · 우리가 하는 일 ============ */}
      <section className="block services" id="services">
        <div className="container">
          <div className="title-group">
            <p className="eyebrow">Services <span className="eyebrow__ko">사업영역</span></p>
            <h2 className="section-title">우리가 하는 일</h2>
            <p className="services__lead">맞춤 시스템 개발부터 IT 위탁 관리, 스마트 협업까지 <br />비즈니스 현장의 모든 IT 문제를 고객의 언어로 풀어드립니다.</p>
          </div>
          <div className="services__grid">
            {/* 01 */}
            <article className="svc-card">
              <div className="svc-card__media">
                <span className="svc-card__tag">01 · SYSTEM PLANNING &amp; INTEGRATION</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18v11H3z"/><path d="M3 9h18"/><path d="M8 20h8"/><path d="M12 16v4"/><circle cx="6" cy="7" r=".6" fill="currentColor" stroke="none"/><path d="M9 12.5l1.5 1.5L9 15.5"/><path d="M13 15.5h2.5"/></svg>
              </div>
              <div className="svc-card__body">
                <p className="svc-card__num">01</p>
                <h3 className="svc-card__title">시스템 기획 및 구축</h3>
                <p className="svc-card__desc">사업 분석부터 업무프로세스 개선, 시스템 구조 설계, 개발 및 테스트 수행.</p>
                <ul className="svc-card__list">
                  <li>비즈니스와 현장의 업무를 깊이 이해하고 최적의 시스템으로 설계</li>
                  <li>업무 프로세스를 분석하고 불필요한 절차와 비효율을 개선</li>
                  <li>업무 흐름을 효율적으로 연결하고 반복 업무를 줄여 생산성을 향상</li>
                  <li>사용자 중심의 편리한 시스템으로 더 나은 업무 경험 제공</li>
                </ul>
              </div>
            </article>
            {/* 02 */}
            <article className="svc-card">
              <div className="svc-card__media">
                <span className="svc-card__tag">02 · MANAGED IT</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="6" rx="1.5"/><rect x="3" y="14" width="18" height="6" rx="1.5"/><path d="M7 7h.01"/><path d="M7 17h.01"/><path d="M15 7h3"/><path d="M15 17h3"/></svg>
              </div>
              <div className="svc-card__body">
                <p className="svc-card__num">02</p>
                <h3 className="svc-card__title">Total IT 아웃소싱</h3>
                <p className="svc-card__desc">시스템 유지보수, 서버 운영, 데이터 보안, 장애 대응까지 전문가가 관리.</p>
                <ul className="svc-card__list">
                  <li>기업환경에 최적화된 IT 전략 운영</li>
                  <li>안정적이고 효율적인 IT 인프라 유지관리</li>
                  <li>장애에 흔들리지 않고 변화에 유연하게 대응하는 비즈니스 연속성 제공</li>
                </ul>
              </div>
            </article>
            {/* 03 */}
            <article className="svc-card">
              <div className="svc-card__media">
                <span className="svc-card__tag">03 · VIDEO COLLABORATION</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="6" width="13" height="11" rx="1.8"/><path d="M15.5 10l6-3v10l-6-3z"/><circle cx="8" cy="10" r="1.6"/><path d="M5.4 14.5c.6-1.4 4.6-1.4 5.2 0"/></svg>
              </div>
              <div className="svc-card__body">
                <p className="svc-card__num">03</p>
                <h3 className="svc-card__title">영상회의 솔루션 구축/운영</h3>
                <p className="svc-card__desc">끊김 없는 고화질 영상·음성 기반 원격 회의 환경 조성을 통한 의사 결정상의 효율성 제공.</p>
                <ul className="svc-card__list">
                  <li>고화질·고음질 화상 연결 솔루션 공급</li>
                  <li>스마트 회의실 구축을 위한 설계 및 설치수행</li>
                  <li>기존 시스템과의 연동설계</li>
                  <li>영상회의 관련 보안솔루션 제공</li>
                </ul>
              </div>
            </article>
          </div>
          <div className="services__ctas">
            <a href="#contact" className="btn-flag btn-flag--primary" onClick={goContact}>문의하기 <ArrowRightSvg /></a>
          </div>
        </div>
      </section>

      {/* ============ Growth · 성장 비전 ============ */}
      <section className="block story" id="growth">
        <div className="container">
          <div className="story__head">
            <div className="title-group">
              <p className="eyebrow">Growth <span className="eyebrow__ko">성장비전</span></p>
              <h2 className="section-title">앞으로의 10년을 만들어 갑니다</h2>
            </div>
            <p className="story__lead">14년간 쌓아온 기술과 실행력을 바탕으로, <br />기술 내재화와 신규 솔루션 출시를 통해 또 다른 성장을 만들어갑니다.</p>
          </div>
          <div className="growth__research">
            <div className="research__grid">
              {RESEARCH_CARDS.map((r) => (
                <div className="research-card" key={r.num}>
                  <p className="research-card__num">{r.num}</p>
                  <h3 className="research-card__title">{r.title}</h3>
                  <p className="research-card__desc">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ Voice · 현장의 목소리로 듣는 방향성 (현재 디자인상 숨김 섹션 — HTML과 동일하게 유지) ============ */}
      <section className="block voice" id="interviews" hidden style={{ display: 'none' }}>
        <div className="container">
          <div className="voice__head title-group">
            <p className="eyebrow">Interviews</p>
            <h2 className="section-title">현장의 목소리로 듣는 방향성</h2>
            <p className="services__lead">지금 각 조직을 이끄는 리더들에게 직접 물었습니다 — 우리가 만드는 효율과 가치의 실체입니다.</p>
          </div>
          <div className="voice__cards">
            {VOICE_CARDS.map((v, i) => (
              <div className="card voice-card" key={i} onClick={() => openVoice(i)}>
                <div className="voice-card__top">
                  <div className="voice-card__avatar">{v.avatar}</div>
                  <p className="voice-card__org">{v.org}</p>
                </div>
                <p className="voice-card__q">{v.q}</p>
                <p className="voice-card__a">{v.a}</p>
                <div className="voice-card__more"><span>자세히 보기 <ChevronRightSvg /></span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Principles · 사훈 + 미션 / 하지 않는 것 ============ */}
      <section className="block principles" id="talent">
        <div className="container">
          <div className="principles__head title-group">
            <p className="eyebrow">Our Principles</p>
            <h2 className="section-title principles__motto"><span className="quote">&ldquo;사람과 행복&rdquo;</span></h2>
            <p className="services__lead">우리가 지향하는 것과 하지 않는 것으로, <br className="br-m" />함께 일하는 기준을 정의합니다.</p>
            <div className="motto-duo">
              <div className="motto-card">
                <span className="motto-card__en" aria-hidden="true">PEOPLE</span>
                <p className="motto-card__label">세상의 주인, 세상의 중심</p>
                <p className="motto-card__word">사람</p>
                <p className="motto-card__desc">세상의 중심은 사람이며, 미래의 다양한 변화에 <br />효과적으로 대응하는 최선의 방법은 사람 중심의 경영</p>
              </div>
              <div className="motto-card">
                <span className="motto-card__en" aria-hidden="true">HAPPINESS</span>
                <p className="motto-card__label">세상을 돌아가게 하는 힘</p>
                <p className="motto-card__word">행복</p>
                <p className="motto-card__desc">조직공동체 내에서 각 구성원의 행복을 극대화하여 <br />세상과 공유할 가치를 창출해 냄</p>
              </div>
            </div>
          </div>
          <div className="principles__list">
            <div className="principles__cols-head" aria-hidden="true">
              <span className="spacer" />
              <div className="cols">
                <span className="principles__col-label">Mission — 우리가 지향하는 것</span>
                <span className="principles__col-label">Mission — 우리가 하지 않는 것</span>
              </div>
            </div>
            {PRINCIPLES.map((p) => (
              <div className="principle" key={p.tile}>
                <div className="principle__tile">{p.tile}</div>
                <div className="principle__cols">
                  <div className="principle__col">
                    <p className="principle__label">{p.label}</p>
                    <p className="principle__headline">
                      {typeof p.value === 'string'
                        ? p.value.split('\n').map((t, i, arr) => (
                            <Fragment key={i}>{t}{i < arr.length - 1 && <br />}</Fragment>
                          ))
                        : p.value}
                    </p>
                    <p className="principle__desc" />
                  </div>
                  <div className="principle__col principle__col--right">
                    <p className="principle__headline" style={{ marginTop: 32 }}>{p.headline}</p>
                    <p className="principle__desc">{p.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Recruit · 인재채용 ============ */}
      <section className="block recruit" id="careers">
        <div className="container">
          <div className="recruit__head title-group">
            <p className="eyebrow">Careers <span className="eyebrow__ko">인재채용</span></p>
            <h2 className="section-title">회사라는 무대 위에서 <br />자기 역할을 찾아가는 사람을 기다립니다.</h2>
            <p className="services__lead">무대 위의 주어진 시간을 활용하여 <br className="br-m" />같이 성장해나가는 사람을 찾습니다.</p>
          </div>

          {/* 인재상 4카드 */}
          <div className="recruit__section">
            <p className="eyebrow eyebrow--sm">Work With Us</p>
            <div className="career-card">
              {CAREER_COLS.map((c) => (
                <div className="career-col" key={c.num}>
                  <div className="career-col__head">
                    <div className="career-col__tile">{c.icon}</div>
                    <div>
                      <p className="career-col__num">{c.num}</p>
                      <p className="career-col__title">{c.title}</p>
                    </div>
                  </div>
                  <p className="career-col__desc">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* RECRUIT PROCESS */}
          <div className="recruit__section">
            <p className="eyebrow eyebrow--sm">RECRUIT PROCESS</p>
            <div className="recruit__section-body">
              <div className="process">
                {PROCESS_STEPS.map((s, i, arr) => (
                  <Fragment key={s.n}>
                    <div className={`process__step${s.final ? ' process__step--final' : ''}`}>
                      <div className="process__head">
                        <div className="process__circle">{s.n}</div>
                        <p className="process__title">{s.title}</p>
                      </div>
                      <div>
                        <p className="process__sub">{s.sub}</p>
                        <p className="process__desc">{s.desc}</p>
                      </div>
                    </div>
                    {i < arr.length - 1 && <ArrowRightSvg className="proc-arrow" />}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Member Interview */}
          <div className="recruit__section" id="culture">
            <p className="eyebrow eyebrow--sm">Member Interview <span className="eyebrow__ko">기업문화</span></p>
            <div className="people__cards-wrap">
              <div className="people__head-row">
                <h2 className="section-title people__title">함께 일하는 사람들의 이야기</h2>
                <div className="people__arrows">
                  <button type="button" aria-label="이전" disabled={prevDisabled} onClick={() => goSlider(-1)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
                  </button>
                  <button type="button" aria-label="다음" disabled={nextDisabled} onClick={() => goSlider(1)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                  </button>
                </div>
              </div>
              <div className="people__cards" ref={trackRef}>
                {PEOPLE_CARDS.map((c, i) => (
                  <div className="story-card" key={i} onClick={() => openStory(i)}>
                    <div
                      className="story-card__thumb"
                      style={{ background: `url(${c.thumb}) ${c.thumbPos} / ${c.thumbSize || 'cover'} no-repeat` }}
                    />
                    <p className="story-card__title">{c.title}</p>
                    <p className="story-card__desc">{c.desc}</p>
                    <div className="story-card__meta"><p className="author">{c.author}</p></div>
                    <div className="story-card__more"><span>자세히 보기 <ChevronRightSvg /></span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 고객이 보내온 메시지 */}
          {/* <div className="recruit__section">
            <p className="eyebrow eyebrow--sm">TESTIMONIALS</p>
            <div className="thanks-grid">
              {TESTIMONIALS.map((t, i) => (
                <div className="thanks-card" key={i}>
                  <span className="thanks-card__quote-mark">&ldquo;</span>
                  <p className="thanks-card__text">{t.text}</p>
                  <p className="thanks-card__from">{t.from}</p>
                </div>
              ))}
            </div>
          </div> */}

          {/* 복리후생 */}
          <div className="recruit__section">
            <p className="eyebrow eyebrow--sm">BENEFITS</p>
            <div className="benefits-grid">
              {BENEFITS.map((b, i) => (
                <div className="benefit-card" key={i}>
                  <div className="benefit-card__head">
                    <div className="benefit-card__texts">
                      <p className="benefit-card__title">{b.title}</p>
                      <p className="benefit-card__sub">{b.sub}</p>
                    </div>
                    <div className="benefit-card__icon">{b.icon}</div>
                  </div>
                  <ul>
                    {b.items.map((it, k) => <li key={k}>{it}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ Contact · 문의하기 ============ */}
      <section className="block contact" id="contact">
        <div className="container">
          <div className="contact__head title-group">
            <p className="eyebrow">Contact <span className="eyebrow__ko">오시는길</span></p>
            <h2 className="section-title">어떤 사업 영역이든, 문의 하나로 충분합니다</h2>
            <p className="contact__lead">문의 내용을 검토한 뒤 7일 이내에 회신드립니다.</p>
          </div>
          <div className="contact-grid">
            <div>
              <div className="office">
                <iframe className="office__map" title="본사 위치 지도" loading="lazy" referrerPolicy="no-referrer-when-downgrade" src="https://maps.google.com/maps?q=%EC%84%9C%EC%9A%B8%20%EA%B0%95%EB%82%A8%EA%B5%AC%20%EB%B4%89%EC%9D%80%EC%82%AC%EB%A1%9C20%EA%B8%B8%2041&z=16&output=embed" />
                <div className="office__body">
                  <p className="office__type">본사 · Headquarters</p>
                  <p className="office__addr">서울특별시 강남구 봉은사로20길 41 (와이앤씨빌딩)</p>
                  <p className="office__line">대표 TEL 02-544-7896 (본사·남부사무소 공통)</p>
                </div>
              </div>
              <div className="office">
                <iframe className="office__map" title="남부사무소 위치 지도" loading="lazy" referrerPolicy="no-referrer-when-downgrade" src="https://maps.google.com/maps?q=%EC%9A%B8%EC%82%B0%EA%B4%91%EC%97%AD%EC%8B%9C%20%EB%8F%99%EA%B5%AC%20%EB%B0%A9%EC%96%B4%EC%A7%84%EC%88%9C%ED%99%98%EB%8F%84%EB%A1%9C%20775&z=16&output=embed" />
                <div className="office__body">
                  <p className="office__type">남부사무소 · South Office</p>
                  <p className="office__addr">울산시 동구 방어진순환도로 775, KT 3층</p>
                </div>
              </div>
              <div className="contacts">
                <p className="contacts__title">사업부별 담당자</p>
                <div className="contacts__list">
                  <p className="contacts__item"><b>AX사업본부 · 이승주 본부장</b><a href="mailto:sjlee@yncsmart.com">sjlee@yncsmart.com</a></p>
                  <p className="contacts__item"><b>기업사업본부 · 김용완 본부장</b><a href="mailto:kyw@yncsmart.com">kyw@yncsmart.com</a></p>
                  <p className="contacts__item"><b>영상회의 솔루션 · 손수영 매니저</b><a href="mailto:syshon@yncsmart.com">syshon@yncsmart.com</a></p>
                  <p className="contacts__item"><b>지능형 SW연구소 · 이향호 소장</b><a href="mailto:leehh@yncsmart.com">leehh@yncsmart.com</a></p>
                  <p className="contacts__item"><b>경영관리본부 · 오주연 유닛장</b><a href="mailto:juyeon1202@yncsmart.com">juyeon1202@yncsmart.com</a></p>
                </div>
              </div>
            </div>
            <div>
              <form className="contact-form" id="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-row">
                  <div>
                    <label htmlFor="cf-name">이름 *</label>
                    <input id="cf-name" name="from_name" type="text" placeholder="홍길동" required onBlur={handleBlur} />
                    {fieldErrors.from_name && <p style={{ color: '#d33', fontSize: 12, marginTop: 4 }}>{fieldErrors.from_name}</p>}
                  </div>
                  <div>
                    <label htmlFor="cf-company">회사명</label>
                    <input id="cf-company" name="company" type="text" placeholder="(주)○○ / 직책" />
                  </div>
                </div>
                <div className="form-row">
                  <div>
                    <label htmlFor="cf-phone">연락처</label>
                    <input id="cf-phone" name="phone" type="tel" placeholder="010-0000-0000" onBlur={handleBlur} />
                    {fieldErrors.phone && <p style={{ color: '#d33', fontSize: 12, marginTop: 4 }}>{fieldErrors.phone}</p>}
                  </div>
                  <div>
                    <label htmlFor="cf-email">이메일 (선택)</label>
                    <input id="cf-email" name="from_email" type="email" placeholder="name@company.com" onBlur={handleBlur} />
                    {fieldErrors.from_email && <p style={{ color: '#d33', fontSize: 12, marginTop: 4 }}>{fieldErrors.from_email}</p>}
                  </div>
                </div>
                <label htmlFor="cf-dept">문의 부서 * <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--primary)', marginLeft: 6 }}>{deptHint}</span></label>
                <select id="cf-dept" name="dept" required defaultValue="" onChange={handleDeptChange}>
                  <option value="" disabled>문의하실 부서를 선택해 주세요</option>
                  {CONTACT_DEPTS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                {fieldErrors.dept && <p style={{ color: '#d33', fontSize: 12, marginTop: 4 }}>{fieldErrors.dept}</p>}
                <label htmlFor="cf-msg">문의 내용 *</label>
                <textarea id="cf-msg" name="message" placeholder="어떤 시스템에 대해 문의하시는지 자유롭게 작성해 주세요." required onBlur={handleBlur} />
                {fieldErrors.message && <p style={{ color: '#d33', fontSize: 12, marginTop: 4 }}>{fieldErrors.message}</p>}
                <div className="form-foot">
                  <label className="agree"><input id="privacy" name="privacy" type="checkbox" required onChange={(e) => setFieldErrors((prev) => ({ ...prev, privacy: e.target.checked ? null : prev.privacy }))} /> 개인정보 수집·이용에 동의합니다 (필수)</label>
                  <button type="submit" className="btn-submit" disabled={sending}>
                    {sending ? '전송 중…' : okVisible ? '접수 완료 ✓' : '문의 보내기'}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4z"/></svg>
                  </button>
                </div>
                {fieldErrors.privacy && <p style={{ color: '#d33', fontSize: 12, marginTop: 4 }}>{fieldErrors.privacy}</p>}
                {sendError && <p style={{ color: '#d33', fontSize: 13, marginTop: 8 }}>{sendError}</p>}
                {okVisible && <div className="form-ok">문의가 접수되었습니다. 경영지원팀에서 담당 부서로 연결해 빠르게 회신드리겠습니다.</div>}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Footer ============ */}
      <footer className="footer">
        <div className="container">
          <div className="footer__top">
            <a className="footer__logo" href="#top"><img src={LOGO} alt="Y&C SmartApps" /></a>
            <nav className="footer__links">
              <a href="#services">사업영역</a>
              <a href="#growth">성장비전</a>
              <a href="#careers">인재채용</a>
              <a href="#culture">기업문화</a>
              <a href="#contact">오시는길</a>
            </nav>
          </div>
          <div className="footer__bottom">
            <span>© 2026 Y&amp;C SmartApps. All rights reserved.</span>
            <span>서울 강남구 봉은사로20길 41 (역삼동, 와이앤씨빌딩) · contact@yncsmart.com</span>
          </div>
        </div>
      </footer>

      {/* ============ Voice Modal ============ */}
      <div className={`vmodal-overlay${voice ? ' is-open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) closeVoice(); }}>
        <div className="vmodal" role="dialog" aria-modal="true">
          <button type="button" className="vmodal__close" aria-label="닫기" onClick={closeVoice}>✕</button>
          {voice && (
            <>
              <div className="vmodal__head">
                <p className="vmodal__badge">{voice.badge}</p>
                <p className="vmodal__org">{voice.org}</p>
              </div>
              <div className="vmodal__body">
                <p className="vmodal__q">{voice.q}</p>
                <div className="vmodal__a">
                  {voice.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ============ Story Modal ============ */}
      <div className={`story-modal-overlay${story ? ' is-open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) closeStory(); }}>
        <div className="story-modal" role="dialog" aria-modal="true">
          <button type="button" className="story-modal__close" aria-label="닫기" onClick={closeStory}>✕</button>
          <div className="story-modal__hero">
            {story && <img src={STORY_HEROES[storyIndex]} alt="" />}
          </div>
          {story && (
            <div className="story-modal__body">
              <span className="story-modal__badge">{story.badge}</span>
              <p className="story-modal__title">{story.title}</p>
              <p className="story-modal__who">{story.who}</p>
              <div className="story-modal__qa">
                {story.qa.map((item, i) => (
                  <div key={i}>
                    <p className="qa__q">{item.q}</p>
                    <div className="qa__a" dangerouslySetInnerHTML={{ __html: item.a.join('') }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
